/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from '@/components/button'
import { Tabs } from '@/components/common/Tab'
import { MainContent } from '@/components/content'
import { ArrowBack } from '@/components/icons'
import { MainLayout } from '@/components/layouts'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { ROUTE_CLUBS, ROUTE_CLUB_MANAGEMENT } from '@/constants/Routes'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { ClubRankings } from '@/pages/club/components/ClubRankings'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { Tab } from '@/types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { getClubDetail, getClubRoles } from '../clubSlice'
import { ClubEvents } from '../components/ClubEvents'
import { ClubInfo } from '../components/ClubInfo'
import { ClubInvites } from '../components/ClubInvites'
import { ClubMatches } from '../components/ClubMatches'
import { ClubMembers } from '../components/ClubMembers'
import { ClubBenefitClaimBanner } from '../components/ClubBenefitClaimBanner'
import { DetailHeader } from '../components/DetailHeader'
import { clubSelector } from '../selectors'

export const ClubDetail = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { isShowDropdown } = useAppSelector(appSelector)
  const { user } = useAppSelector(userSelector)
  const { permissions, club } = useAppSelector(clubSelector)
  const { token } = useAppSelector(authSelector)

  const { clubId } = params

  const [tabs, setTabs] = useState<Tab | null>(null)
  const [viewInfo, setViewInfo] = useState(false)

  const isAccountSetup =
    club?.accountStatus?.accountId && !club?.accountStatus?.pendingRequirement && club?.accountStatus?.detailsSubmitted

  const handleBack = () => {
    if (!viewInfo) {
      // Check if we came from club management page
      const state = location.state as { from?: string } | null
      if (state?.from === ROUTE_CLUB_MANAGEMENT) {
        navigate(ROUTE_CLUB_MANAGEMENT)
      } else {
        navigate(ROUTE_CLUBS)
      }
    }
    if (viewInfo) setViewInfo(false)
  }

  const onNavigateEditClub = () => {
    navigate(`${ROUTE_CLUBS}/${clubId}/edit`)
  }

  const onViewInfo = () => setViewInfo(true)

  const onGetData = () => {
    if (clubId && user?.id) {
      dispatch(getClubRoles({ clubId: Number(clubId), userId: user?.id }))
      dispatch(getClubDetail(clubId))
    }
  }

  useEffect(() => {
    onGetData()
  }, [clubId, user])

  useEffect(() => {
    if (permissions) {
      const invitePermission = permissions?.filter((item) => item === 'CLUB_MEMBERS::ADD')

      setTabs({
        Rankings: { tabIsUse: true, isShow: true },
        Members: { tabIsUse: false, isShow: true },
        Matches: { tabIsUse: false, isShow: true },
        Events: { tabIsUse: false, isShow: true },
        Invites: { tabIsUse: false, isShow: invitePermission && !!invitePermission.length },
      })
    }
  }, [permissions])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent>
          <div className="flex flex-col gap-y-6 mb-20 lg:mb-8">
            <div className="flex items-center">
              <Button onClick={handleBack} className="hover:opacity-50">
                <ArrowBack className="text-white" />
              </Button>
              <h1 className="ml-3 text-sm md:text-xl text-white font-semibold">
                {viewInfo ? t('club.about') : t('club.clubDetails')}
              </h1>
            </div>

            {viewInfo && club ? (
              <ClubInfo club={club} isPage />
            ) : (
              <>
                <DetailHeader
                  onNavigateEditClub={onNavigateEditClub}
                  isAccountSetup={isAccountSetup}
                  onViewInfo={onViewInfo}
                />

                {/* The name is load-bearing, not decoration: the banner and the modal's confirm
                    button both read "Claim at {clubName}", so `?? ''` rendered "Claim at " and
                    asked a member to commit a benefit — irreversibly, to one club — to somewhere
                    the page could not name. Waiting for the name is the only honest option. */}
                {token && club?.clubId && club?.clubName && (
                  <ClubBenefitClaimBanner clubId={Number(club.clubId)} clubName={club.clubName} />
                )}

                {token && user?.isValidEmail && (
                  <div className="flex flex-col gap-y-4">
                    <h2 className="text-xl text-white font-semibold">{t('common.details')}</h2>
                    <div className="flex gap-x-6">
                      {tabs && (
                        <div className="flex flex-col w-full md:w-2/3 gap-y-4">
                          <Tabs tabs={tabs} setTabs={setTabs} />

                          {tabs.Rankings.tabIsUse && <ClubRankings />}

                          {tabs.Members.tabIsUse && <ClubMembers />}

                          {tabs.Matches.tabIsUse && <ClubMatches />}

                          {tabs.Events.tabIsUse && <ClubEvents />}

                          {tabs.Invites.tabIsUse && <ClubInvites />}
                        </div>
                      )}
                      {club && <ClubInfo club={club} />}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </MainContent>
      </MainLayout>
    </>
  )
}
